'use strict'

import Vue from 'vue'
import Api from '@/api/voicebot.js'
import Vox from '@/api/voximplant-websdk.js'

const getNextIndex = (function () {
  let identifier = 0
  return () => {
    return identifier++
  }
})()

const setQuestionNames = function () {
  state.voicebot.questions.forEach((item, index) => { item.name = `Question ${index + 1}` })
}

const audioConnectWithOptions = function (options) {
  Vox.connect(options)
}

const state = {
  loading: true,
  error: null,
  voicebot: null,
  options: [],
  deletedQuestions: [],
  route: null
}

const actions = {
  fetch ({commit}, {vacancyId}) {
    if (!vacancyId) {
      throw new Error('Empty vacancy id')
    }

    commit('fetch')

    return Promise.all([
      Api.fetchOneByVacancyId(vacancyId),
      Api.fetchOptions()
    ]).then(data => {
      commit('fetchSuccess', data[0])
      commit('fetchOptionsSuccess', data[1])
      audioConnectWithOptions(data[0].options)
    }).catch(err => commit('fetchFailure', err))
  },

  addQuestion ({commit}, model) {
    // commit('addQuestion')
    const newQuestion = {name: 'name', body: 'New question', is_required: true}
    return Api.storeQuestions(model.id, [newQuestion])
      .then(() => Api.fetchOneByVacancyId(model.id))
      .then(data => commit('fetchSuccess', data))
  },

  removeQuestion ({commit}, { question }) {
    return Api.removeQuestions(state.voicebot.id, [question])
      .then(() => Api.fetchOneByVacancyId(state.voicebot.id))
      .then(data => commit('fetchSuccess', data))
  },

  updateQuestions ({commit}, { questions }) {
    return Api.updateQuestions(state.voicebot.id, questions)
      .then(() => Api.fetchOneByVacancyId(state.voicebot.vacancy.id))
      .then(data => commit('fetchSuccess', data))
  },

  save ({commit, dispatch, state, rootGetters}, model) {
    if (!model.id) {
      throw new Error('Voicebot empty id')
    }

    commit('save')

    Api.updateOne(model)
      .then(() => {
        const questionsToDelete = state.deletedQuestions
        const questionsToCreate = (model.questions || []).filter(question => !question.id)
        const questionsToUpdate = (model.questions || []).filter(question => question.id)
        const options = (model.options || [])

        return Promise.all([
          questionsToDelete.length
            ? Api.removeQuestions(model.id, questionsToDelete)
            : null,
          questionsToCreate.length
            ? Api.storeQuestions(model.id, questionsToCreate)
            : null,
          questionsToUpdate.length
            ? Api.updateQuestions(model.id, questionsToUpdate)
            : null,
          options.length
            ? Api.updateOptions(model.id, options)
            : null
        ])
      })
      .then(() => dispatch('fetch', {
        vacancyId: model.vacancy.id
      }))
      .then(() => {
        if (!rootGetters['vacancy/one'].is_published) {
          commit('updateRoute', {
            name: 'vacancy-videointerview',
            params: {
              id: model.vacancy.id
            }
          })
        }
      })
      .catch(err => commit('saveFailure', err))
  },

  disable ({commit, state}, model) {
    commit('save')

    const data = {
      id: model.id,
      is_active: false
    }

    return Api.updateOne(data)
      .then(data => commit('saveSuccess', data))
      .then(() => commit('updateRoute', {
        name: 'vacancy-videointerview',
        params: {
          id: model.vacancy.id
        }
      }))
      .catch(err => commit('saveFailure', err))
  },

  enable ({commit, state}, model) {
    commit('save')

    const data = {
      id: model.id,
      is_active: true
    }

    return Api.updateOne(data)
      .then(data => commit('saveSuccess', data))
      .then(() => commit('updateRoute', {
        name: 'vacancy-videointerview',
        params: {
          id: model.vacancy.id
        }
      }))
    .catch(err => commit('saveFailure', err))
  },

  playAudio ({commit, state}, model, dispatch) {
    Vox.say(model, commit, dispatch)
    commit('audioStarted', model)
  },

  stopAudio ({commit, state}, model) {
    Vox.stop()
    commit('audioStopped', model)
  },

  initAudio (state, options) {
    Vox.connect(options)
  },

  destroyAudio (state, model) {
    Vox.disconnect()
  }
}

const mutations = {
  fetch (state) {
    // state.voicebot = null
    state.loading = true
  },

  fetchSuccess (state, data) {
    state.voicebot = data
    state.deletedQuestions = []
    state.loading = false
  },

  fetchFailure (state, err) {
    state.error = err
    state.loading = false
    console.error(err)
  },

  updateQuestion (state, {model, data}) {
    Object.assign(model, data)
  },

  addQuestion (state) {
    state.voicebot && state.voicebot.questions.push({
      name: '',
      body: '',
      index: getNextIndex()
    })

    setQuestionNames()
  },

  audioStarted (state, model) {
    // model.playing = true
    // let index = state.voicebot.questions.findIndex(item => item.id === model.id)
    // Vue.set(state.voicebot.questions, index, model)
    state.voicebot.questions = state.voicebot.questions
      .map(question => ({ ...question, playing: question.id === model.id }))
  },

  audioStopped (state, model) {
    model.playing = false
    let index = state.voicebot.questions.findIndex(item => item.id === model.id)
    Vue.set(state.voicebot.questions, index, model)
    // state.voicebot.questions = state.voicebot.questions
    //   .map(question => ({ ...question, playing: false }))
  },

  deleteQuestion (state, {model}) {
    let index

    if (model.id) {
      index = state.voicebot.questions.findIndex(item => item.id === model.id)
    } else {
      index = state.voicebot.questions.findIndex(item => item.index === model.index)
    }

    state.deletedQuestions.push(model)

    Vue.delete(state.voicebot.questions, index)

    setQuestionNames()
  },

  fetchOptionsSuccess (state, data) {
    state.options = data
  },

  updateOption (state, {model, data}) {
    Object.assign(model, data)
    Vox.updateConfig(model.name, model.values[0].name)
  },

  save (state) {
    state.loading = true
  },

  saveSuccess (state, data) {
    state.voicebot = data
    state.loading = false
  },

  saveFailure (state, err) {
    state.loading = false
    state.error = err
    console.error(err)
  },

  updateRoute (state, data) {
    state.route = data
  }
}

const getters = {
  error (state, getters) {
    return state.error
  },

  loading (state, getters) {
    return state.loading
  },

  one (state, getters) {
    return state.voicebot || {}
  },

  selectedOptions (state, getters) {
    return state.voicebot ? state.voicebot.options : []
  },

  options (state, getters) {
    return state.options
  },

  route (state, getters) {
    return state.route
  }
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
}
